import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { assistantApi, documentsApi, jobsApi, usersApi } from "../services/api";
import { downloadBlob } from "../utils/download";
import { USAGE_EVENT } from "../services/http";

const AppContext = createContext(null);
const EMPTY_DOCS = { resumes: [], templates: [], transcripts: [], emailTemplates: [] };
const NO_INTEGRATIONS = {
  ai: false,
  configured: { ai: false },
  access: { allowed: false, status: "none", note: "" },
  limits: null,
  search: { startups: true },
};

let toastId = 0;

export function AppProvider({ children }) {
  const { user, setUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [documents, setDocuments] = useState(EMPTY_DOCS);
  const [status, setStatus] = useState("loading");
  const [loadError, setLoadError] = useState("");
  const [toasts, setToasts] = useState([]);
  const [addJob, setAddJob] = useState({ open: false, url: "" });
  const [integrations, setIntegrations] = useState(NO_INTEGRATIONS);

  const jobsRef = useRef(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  const toast = useCallback((message, variant = "success") => {
    const id = ++toastId;
    setToasts((list) => [...list, { id, message, variant }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 3500);
  }, []);

  const dismissToast = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const [{ jobs }, docs, services] = await Promise.all([
        jobsApi.list(),
        documentsApi.list(),
        assistantApi.status().catch(() => NO_INTEGRATIONS),
      ]);
      setJobs(jobs);
      setDocuments({ ...EMPTY_DOCS, ...docs });
      setIntegrations(services);
      setStatus("ready");
    } catch (err) {
      setLoadError(err.message);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () =>
      assistantApi
        .status()
        .then(setIntegrations)
        .catch(() => {});
    window.addEventListener(USAGE_EVENT, refresh);
    return () => window.removeEventListener(USAGE_EVENT, refresh);
  }, []);

  const refreshDocuments = useCallback(async () => setDocuments({ ...EMPTY_DOCS, ...(await documentsApi.list()) }), []);

  const replaceJob = useCallback((job) => setJobs((list) => list.map((j) => (j.id === job.id ? job : j))), []);

  // Applies an optimistic change, then swaps in the server's copy. Rolls back and shows a toast on failure.
  // Resolves to the saved job, or null if the request failed.
  const mutateJob = useCallback(
    async (id, optimistic, call) => {
      const prev = jobsRef.current.find((j) => j.id === id);
      if (optimistic && prev) {
        const patch = typeof optimistic === "function" ? optimistic(prev) : optimistic;
        setJobs((list) => list.map((j) => (j.id === id ? { ...j, ...patch } : j)));
      }
      try {
        const { job } = await call();
        replaceJob(job);
        return job;
      } catch (err) {
        if (prev) replaceJob(prev);
        toast(err.message, "danger");
        return null;
      }
    },
    [replaceJob, toast]
  );

  const attempt = useCallback(
    async (fn) => {
      try {
        return (await fn()) ?? true;
      } catch (err) {
        toast(err.message, "danger");
        return null;
      }
    },
    [toast]
  );

  const actions = useMemo(
    () => ({
      toast,
      dismissToast,
      reload: load,

      refreshJobs: () =>
        attempt(async () => {
          const { jobs } = await jobsApi.list();
          setJobs(jobs);
        }),

      // Picks up changes made elsewhere (like the extension marking a job applied) without a toast on failure.
      // Unchanged jobs keep their objects so open editors don't reset.
      syncJobs: async () => {
        try {
          const [{ jobs: fresh }, services] = await Promise.all([jobsApi.list(), assistantApi.status()]);
          const known = new Map(jobsRef.current.map((j) => [j.id, j]));
          setJobs(fresh.map((j) => (known.get(j.id)?.updatedAt === j.updatedAt ? known.get(j.id) : j)));
          setIntegrations(services);
        } catch {
          // Offline or signed out; the next focus tries again.
        }
      },

      refreshStatus: () =>
        assistantApi
          .status()
          .then(setIntegrations)
          .catch(() => {}),

      // Resolves to true once the request is recorded; shows a toast and resolves to null on failure.
      requestAccess: (reason) =>
        attempt(async () => {
          const { user } = await usersApi.requestAccess(reason);
          setUser(user);
          setIntegrations(await assistantApi.status());
          return true;
        }),
      openAddJob: (url = "") => setAddJob({ open: true, url }),
      closeAddJob: () => setAddJob({ open: false, url: "" }),

      // Throws on failure so forms can show the error inline.
      createJob: async (fields, stage = "found") => {
        const { job } = await jobsApi.create({ ...fields, stage });
        setJobs((list) => [job, ...list]);
        return job.id;
      },

      updateJob: (id, changes) => mutateJob(id, changes, () => jobsApi.update(id, changes)),

      moveJob: (id, stage) => mutateJob(id, { stage }, () => jobsApi.setStage(id, stage)),

      completeStep: (id, step, activity, updates) =>
        mutateJob(
          id,
          (prev) => ({ ...updates, steps: { ...prev.steps, [step]: true } }),
          () => jobsApi.completeStep(id, step, { activity, updates })
        ),

      undoApplied: (id) =>
        mutateJob(
          id,
          (prev) => ({ steps: { ...prev.steps, applied: false }, appliedAt: null, stage: prev.stage === "applied" ? "found" : prev.stage }),
          () => jobsApi.undoApplied(id)
        ),

      logActivity: (id, text) => mutateJob(id, null, () => jobsApi.addActivity(id, text)),

      deleteJob: async (id) => {
        const prev = jobsRef.current;
        setJobs((list) => list.filter((j) => j.id !== id));
        try {
          await jobsApi.remove(id);
          return true;
        } catch (err) {
          setJobs(prev);
          toast(err.message, "danger");
          return false;
        }
      },

      updateProfile: (profile) =>
        attempt(async () => {
          const { user } = await usersApi.update({ profile });
          setUser(user);
        }),

      dismissOnboarding: () => {
        setUser((u) => ({ ...u, onboardingDismissed: true }));
        usersApi.update({ onboardingDismissed: true }).catch(() => {});
      },

      uploadResume: (file) =>
        attempt(async () => {
          const { resume } = await documentsApi.uploadResume(file);
          setDocuments((d) => ({ ...d, resumes: [...d.resumes, resume] }));
          return resume;
        }),

      downloadResume: (resume) =>
        attempt(async () => downloadBlob(resume.name, await documentsApi.downloadResume(resume.id))),

      uploadTranscript: (file) =>
        attempt(async () => {
          const { transcript } = await documentsApi.uploadTranscript(file);
          setDocuments((d) => ({ ...d, transcripts: [...d.transcripts, transcript] }));
          return transcript;
        }),

      downloadTranscript: (transcript) =>
        attempt(async () => downloadBlob(transcript.name, await documentsApi.downloadTranscript(transcript.id))),

      addTemplate: (template) =>
        attempt(async () => {
          const { template: saved } = await documentsApi.createTemplate(template);
          setDocuments((d) => ({ ...d, templates: [...d.templates, saved] }));
          return saved;
        }),

      addEmailTemplate: (template) =>
        attempt(async () => {
          const { emailTemplate: saved } = await documentsApi.createEmailTemplate(template);
          setDocuments((d) => ({ ...d, emailTemplates: [...(d.emailTemplates || []), saved] }));
          return saved;
        }),

      updateTemplate: (id, changes) =>
        attempt(async () => {
          const { template: saved } = await documentsApi.updateTemplate(id, changes);
          setDocuments((d) => ({ ...d, templates: d.templates.map((t) => (t.id === id ? saved : t)) }));
          return saved;
        }),

      updateEmailTemplate: (id, changes) =>
        attempt(async () => {
          const { emailTemplate: saved } = await documentsApi.updateEmailTemplate(id, changes);
          setDocuments((d) => ({
            ...d,
            emailTemplates: (d.emailTemplates || []).map((t) => (t.id === id ? saved : t)),
          }));
          await refreshDocuments().catch(() => {});
          return saved;
        }),

      removeDocument: (kind, id) =>
        attempt(async () => {
          await documentsApi.remove(kind, id);
          await refreshDocuments();
        }),

      setDefaultDocument: (kind, id) => {
        // Email templates have one default per recipient kind, so let the server say which stay default.
        if (kind !== "emailTemplates") {
          setDocuments((d) => ({ ...d, [kind]: d[kind].map((doc) => ({ ...doc, isDefault: doc.id === id })) }));
        }
        return attempt(() => documentsApi.setDefault(kind, id)).then(async (ok) => {
          await refreshDocuments().catch(() => {});
          return ok;
        });
      },

      loadSampleData: () =>
        attempt(async () => {
          await usersApi.loadSampleData();
          await load();
        }),

      startFresh: () =>
        attempt(async () => {
          const { user } = await usersApi.resetData();
          setUser(user);
          await load();
        }),
    }),
    [toast, dismissToast, load, mutateJob, attempt, refreshDocuments, setUser]
  );

  const value = useMemo(
    () => ({
      user,
      profile: user?.profile ?? {},
      onboardingDismissed: user?.onboardingDismissed ?? false,
      jobs,
      documents,
      status,
      loadError,
      toasts,
      addJob,
      integrations,
      ...actions,
    }),
    [user, jobs, documents, status, loadError, toasts, addJob, integrations, actions]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
  return ctx;
}

// Re-syncs jobs when you come back to this tab, e.g. after submitting an application in another one.
export function useSyncOnFocus() {
  const { syncJobs } = useApp();
  useEffect(() => {
    let last = 0;
    const onFocus = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 3000) return;
      last = Date.now();
      syncJobs();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [syncJobs]);
}
